import React from 'react';

import { type UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { CURRENCIES } from 'src/features/payStations/types';
import ReservationDurationDetails from 'src/features/reservations/components/forms/ReservationDurationDetails';
import ReservationNameTypeDate from 'src/features/reservations/components/forms/ReservationNameTypeDate';
import ReservationPricing from 'src/features/reservations/components/forms/ReservationPricing';
import ReservationStayDetails from 'src/features/reservations/components/forms/ReservationStayDetails';
import { type CreateFixedReservationTypeFormSchemaValues } from 'src/features/reservations/hooks/useCreateReservationType';
import { useDisplayedFormValues } from 'src/features/reservations/providers/ReservationVersionsProvider';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { formatPrice } from 'src/features/reservations/utils/utils';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages } from 'src/utils/messages';

interface FixedDurationTypeFormProps {
    form: UseFormReturn<CreateFixedReservationTypeFormSchemaValues>;
    onSubmit: () => void;
}

function FixedDurationTypeForm({ form, onSubmit }: FixedDurationTypeFormProps) {
    const { setDisplayedFormValues } = useDisplayedFormValues();
    const {
        price,
        tax_amount,
        price_with_tax,
        tax_percent,
        currency,
        valid_from,
        valid_to,
        fixed_duration_start,
        fixed_duration_end,
        paid,
    } = form.getValues();

    const { formatMessage } = useIntl();
    const [selectedFromDate, setSelectedFromDate] = React.useState<Date | undefined>(valid_from);
    const [selectedToDate, setSelectedToDate] = React.useState<Date | undefined>(valid_to);
    const [selectedDurationStartDate, setSelectedDurationStartDate] = React.useState<Date | undefined>(
        fixed_duration_start,
    );
    const [selectedDurationEndDate, setSelectedDurationEndDate] = React.useState<Date | undefined>(fixed_duration_end);

    React.useEffect(() => {
        setDisplayedFormValues({
            price: price ? formatPrice(price) : '',
            tax_percent: tax_percent ? (parseFloat(tax_percent) * 100).toFixed(2) : '',
            tax_amount: tax_amount ? formatPrice(tax_amount) : '',
            price_with_tax: price_with_tax ? formatPrice(price_with_tax) : '',
            currency: currency ?? CURRENCIES.EUR,
        });
    }, [form]);

    const BooleanOptions = {
        false: formatMessage(generalMessages.no),
        true: formatMessage(generalMessages.yes),
    };

    return (
        <Form form={form} variant="dialog" onSubmit={form.handleSubmit(onSubmit)}>
            <ReservationNameTypeDate
                selectedFromDate={selectedFromDate}
                setSelectedFromDate={setSelectedFromDate}
                selectedToDate={selectedToDate}
                setSelectedToDate={setSelectedToDate}
                showValidToDate
            />
            <FormField
                control={form.control}
                name="paid"
                render={() => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel required variant="dialog">
                                {formatMessage(reservationMessages.paid)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Select
                                        onValueChange={(val) => {
                                            const isPaid = val === 'true';
                                            form.setValue('paid', isPaid, { shouldDirty: true, shouldValidate: true });

                                            if (!isPaid) {
                                                // Zero out the pricing so an unpaid version is submitted with 0 instead of stale/null prices
                                                form.setValue('price', 0, { shouldDirty: true, shouldValidate: true });
                                                form.setValue('price_with_tax', 0, {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                });
                                                form.setValue('tax_amount', 0, {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                });
                                                setDisplayedFormValues((prev) => ({
                                                    ...prev,
                                                    price: '',
                                                    price_with_tax: '',
                                                    tax_amount: '',
                                                }));
                                            }
                                        }}
                                        value={(paid ?? true).toString()}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder={formatMessage(reservationMessages.paid)} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(BooleanOptions).map(([key, value]) => {
                                                return (
                                                    <SelectItem key={key} value={key}>
                                                        {value}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            {paid && <ReservationPricing />}
            <ReservationStayDetails />
            <ReservationDurationDetails
                selectedStartDate={selectedDurationStartDate}
                setSelectedStartDate={setSelectedDurationStartDate}
                selectedEndDate={selectedDurationEndDate}
                setSelectedEndDate={setSelectedDurationEndDate}
            />
        </Form>
    );
}

export { FixedDurationTypeForm };
